import { useCallback, useState } from 'react';

// Shared column-sort state for the table pages. A page supplies a cellValue
// accessor (string | number) per column; numbers sort numerically, everything
// else sorts case-insensitively. Mirrors the inline pattern on Receiving Log.

export interface SortState {
  column: string | null;
  direction: 'asc' | 'desc';
}

export function useSort(initial: SortState = { column: null, direction: 'asc' }) {
  const [sort, setSort] = useState<SortState>(initial);

  // Click a column: first click sorts ascending, repeat clicks flip direction.
  const toggle = useCallback((column: string) => {
    setSort(prev => prev.column === column
      ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'asc' });
  }, []);

  return { sort, toggle, setSort };
}

export function sortRows<T>(rows: T[], sort: SortState, getValue: (row: T, column: string) => string | number): T[] {
  if (!sort.column) return rows;
  const column = sort.column;
  return [...rows].sort((a, b) => {
    const av = getValue(a, column);
    const bv = getValue(b, column);
    let cmp: number;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
    }
    return sort.direction === 'asc' ? cmp : -cmp;
  });
}
